import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileSignature } from "lucide-react";
import { AgreementBody } from "@/components/agreements/AgreementCommon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e) => e?.response?.data?.detail || "Something went wrong. Please try again.";

export default function SignAgreement() {
  const { token } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/public/agreement/${token}`).then((r) => setA(r.data)).catch((e) => setErr(errText(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const sign = async () => {
    setBusy(true);
    try { await axios.post(`${API}/public/agreement/${token}/sign`, { full_name: name, agree }); toast.success("Thank you, the agreement is signed"); load(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-ivory">
      <header className="border-b border-border bg-white"><div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4"><img src="/logo.png" alt="" className="h-9 w-9" /><div><p className="font-heading font-bold text-ink">Brightside Befriending Services</p><p className="text-xs text-ink-muted">Service agreement</p></div></div></header>
      <main className="mx-auto max-w-3xl px-4 py-8" data-testid="public-sign-page">
        {err && <p className="rounded-xl border border-coral/30 bg-coral-light/40 p-6 text-center text-coral-dark" data-testid="public-sign-error">{err}</p>}
        {!a && !err && <div className="flex h-48 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" /></div>}
        {a && (
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-3xl font-bold text-ink">{a.client_name}'s befriending agreement</h1>
              <p className="mt-1 text-ink-soft">{a.nok_name ? `For ${a.nok_name} · ` : ""}Version {a.version}</p>
            </div>
            <div className="rounded-xl border border-border bg-white p-5 shadow-sm"><AgreementBody a={a} /></div>
            {a.status === "signed" ? (
              <p className="flex items-center gap-2 rounded-xl bg-sage-light p-5 text-sage-dark" data-testid="public-signed"><CheckCircle2 size={20} /> Thank you, this agreement is signed by {a.signed_by_name}. We'll be in touch to arrange the first visit.</p>
            ) : (
              <div className="space-y-3 rounded-xl border border-brand/30 bg-white p-5 shadow-sm" data-testid="public-sign-form">
                <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-ink"><FileSignature size={18} className="text-brand" /> Sign online</h2>
                <label className="flex items-start gap-2 text-sm text-ink"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="public-sign-agree" /> I have read this agreement and agree to it on behalf of {a.client_name}.</label>
                <label className="block text-xs text-ink-muted">Type your full name
                  <input value={name} onChange={(e) => setName(e.target.value)} data-testid="public-sign-name" className="mt-1 block w-full max-w-sm rounded-lg border border-border px-3 py-2 text-sm text-ink outline-none focus:border-brand" />
                </label>
                <button disabled={busy || !agree || name.trim().length < 3} onClick={sign} data-testid="public-sign-button" className="rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Signing…" : "Sign agreement"}</button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
